import React from 'react'
import {
    Box, Button, IconButton, LinearProgress, MenuItem, Table, TableBody, TableCell, TableContainer,
    TableHead, TablePagination, TableRow, TextField, Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined'
import { useNavigate } from 'react-router-dom'
import { ApiPushGetCampaigns } from '../../apiService/Core/Apicall'
import PushPageHeader from '../../Components/Push/PushPageHeader'
import PushPanel from '../../Components/Push/PushPanel'
import PushDisabledNotice, { InlineAlert } from '../../Components/Push/PushDisabledNotice'
import StatusPill from '../../Components/Push/StatusPill'
import { useGuardedFetch } from '../../Components/Push/useGuardedFetch'
import { formFieldSx, headerBtnSx, paginationStyle, selectProps, tableSx, tdStyle, thStyle, trHover } from '../../Components/Push/pushStyles'
import {
    ACTIVE_CAMPAIGN_STATUSES, CAMPAIGN_STATUSES, fmtDate, fmtNum, getErrorMessage, isPushDisabled,
    LIST_POLL_INTERVAL_MS, qs,
} from '../../Components/Push/pushUtils'
import { usePersistedState } from '../../hooks/userPersistedState'
import type { Campaign, Pagination } from '../../Components/Push/types'

const HEADERS = ['Title', 'Status', 'Sender', 'Created', 'Completed', 'Targeted', 'Accepted by Firebase', 'Failed', 'Skipped', 'Progress', 'Action']

const statusLabel = (s: string) =>
    s === 'COMPLETED_WITH_FAILURES' ? 'Completed with failures' : s.charAt(0) + s.slice(1).toLowerCase()

const Notifications: React.FC = () => {
    const navigate = useNavigate()
    const [page, setPage] = usePersistedState('notifications:page', 0)
    const [rowsPerPage, setRowsPerPage] = usePersistedState('notifications:rowsPerPage', 10)
    const [status, setStatus] = usePersistedState('notifications:status', '')
    // isTest filter: "false" campaigns, "true" test sends, "" all
    const [kind, setKind] = usePersistedState('notifications:kind', 'false')

    const fetcher = React.useCallback(
        (silent: boolean) => ApiPushGetCampaigns(qs({ page: page + 1, limit: rowsPerPage, status, isTest: kind }), { silent }),
        [page, rowsPerPage, status, kind]
    )
    const [hasActive, setHasActive] = React.useState(false)
    const { data, error, loading, refetch } = useGuardedFetch<any>(fetcher, {
        pollMs: hasActive ? LIST_POLL_INTERVAL_MS : 0,
    })

    const rows: Campaign[] = data?.data?.items ?? []
    const pagination: Pagination | undefined = data?.meta?.pagination
    const total = pagination?.total ?? 0

    React.useEffect(() => {
        const items: Campaign[] = data?.data?.items ?? []
        setHasActive(items.some((r) => ACTIVE_CAMPAIGN_STATUSES.includes(r.status)))
    }, [data])

    // keep the persisted page valid if the result set shrinks
    React.useEffect(() => {
        if (data && page > 0 && rows.length === 0) setPage(Math.max(0, Math.ceil(total / rowsPerPage) - 1))
    }, [data, page, rows.length, total, rowsPerPage, setPage])

    const disabled = isPushDisabled(error)

    return (
        <div className="common-layout flex-column">
            <PushPageHeader
                title="Notifications"
                actions={
                    <>
                        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refetch} sx={headerBtnSx}>Refresh</Button>
                        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => navigate('/admin/notifications/new')} sx={headerBtnSx}>
                            New campaign
                        </Button>
                    </>
                }
            />

            {disabled ? (
                <PushDisabledNotice />
            ) : (
                <PushPanel
                    title="Campaign History"
                    icon={<NotificationsActiveOutlinedIcon sx={{ color: '#ff6a3d', fontSize: 20 }} />}
                    sx={{ mt: 2 }}
                    bodySx={{ p: 0 }}
                    action={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <TextField
                                select size="small" value={status} label="Status"
                                onChange={(e) => { setStatus(e.target.value); setPage(0) }}
                                sx={{ ...formFieldSx, minWidth: 220 }}
                                slotProps={{ select: selectProps }}
                            >
                                <MenuItem value="">All statuses</MenuItem>
                                {CAMPAIGN_STATUSES.map((s) => <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>)}
                            </TextField>
                            <TextField
                                select size="small" value={kind} label="Type"
                                onChange={(e) => { setKind(e.target.value); setPage(0) }}
                                sx={{ ...formFieldSx, minWidth: 160 }}
                                slotProps={{ select: selectProps }}
                            >
                                <MenuItem value="false">Campaigns</MenuItem>
                                <MenuItem value="true">Test sends</MenuItem>
                                <MenuItem value="">All</MenuItem>
                            </TextField>
                        </Box>
                    }
                >
                    {error && <Box sx={{ p: 2 }}><InlineAlert severity="error">{getErrorMessage(error)}</InlineAlert></Box>}
                    <Box sx={{ height: 3 }}>{loading && <LinearProgress sx={{ height: 3, background: 'transparent', '& .MuiLinearProgress-bar': { background: '#ff6a3d' } }} />}</Box>
                    <TableContainer sx={{ maxHeight: '60vh' }}>
                        <Table stickyHeader sx={tableSx}>
                            <TableHead>
                                <TableRow>{HEADERS.map((h) => <TableCell key={h} sx={thStyle}>{h}</TableCell>)}</TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.length > 0 ? rows.map((c) => (
                                    <TableRow key={c.id} sx={{ ...trHover, cursor: 'pointer' }} onClick={() => navigate(`/admin/notifications/${c.id}`)}>
                                        <TableCell sx={{ ...tdStyle, maxWidth: 260 }}>
                                            <Typography fontSize={13} fontWeight={600} color="#e0e0e0" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {c.title}{c.isTest ? ' (test)' : ''}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={tdStyle}><StatusPill status={c.status} /></TableCell>
                                        <TableCell sx={tdStyle}>{c.createdBy?.name ?? '-'}</TableCell>
                                        <TableCell sx={tdStyle}>{fmtDate(c.createdAt)}</TableCell>
                                        <TableCell sx={tdStyle}>{fmtDate(c.completedAt)}</TableCell>
                                        <TableCell sx={tdStyle}>{fmtNum(c.counters?.matched)}</TableCell>
                                        <TableCell sx={{ ...tdStyle, color: '#10b981', fontWeight: 600 }}>{fmtNum(c.counters?.sent)}</TableCell>
                                        <TableCell sx={{ ...tdStyle, color: '#ef4444' }}>{fmtNum(c.counters?.failed)}</TableCell>
                                        <TableCell sx={{ ...tdStyle, color: '#f59e0b' }}>{fmtNum(c.counters?.skipped)}</TableCell>
                                        <TableCell sx={tdStyle}>{c.progress?.percent ?? 0}%</TableCell>
                                        <TableCell sx={tdStyle}>
                                            <Tooltip title="View campaign" arrow>
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/admin/notifications/${c.id}`) }}>
                                                    <VisibilityOutlinedIcon sx={{ color: '#ff6a3d', fontSize: 18 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={HEADERS.length} align="center" sx={{ py: 8, background: '#1a1a1a', border: 'none' }}>
                                            <NotificationsActiveOutlinedIcon sx={{ color: '#2f2f2f', fontSize: 40, mb: 1 }} />
                                            <Typography color="#4a4a4a" fontSize={13}>{loading ? 'Loading campaigns...' : 'No campaigns yet.'}</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div" count={total} page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
                        rowsPerPageOptions={[5, 10, 25, 50]} showFirstButton showLastButton
                        sx={paginationStyle}
                    />
                </PushPanel>
            )}
        </div>
    )
}

export default Notifications
